import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import Image from '@/components/Image';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import LinkFormModal from '@/pages/LinksLibrary/components/LinkFormModal';
import { LibraryLinkResource } from '@/types/resources';
import { api } from '@/utils/api';

const Content = styled.div`
  display: flex;
  align-items: center;
`;

const Thumbnail = styled(Image)`
  margin-right: 16px;
  flex-shrink: 0;
`;

const Title = styled(Text)`
  margin-bottom: 4px;
`;

const ContentText = styled.div`
  flex: 1;
  overflow-x: hidden;
  text-overflow: ellipsis;
  a {
    white-space: nowrap;
  }
`;

interface Props {
  libraryLink: LibraryLinkResource;
}

const LinksTableRow: FC<Props> = ({ libraryLink }) => {
  const [showEdit, setShowEdit] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete this ${libraryLink.type}?`,
        libraryLink.name,
      );

      if (!confirm) return;

      const { id } = await api.delete<never, { id: number }>(
        `/v1/library-links/${libraryLink.id}`,
      );

      queryClient.setQueryData(
        ['library-links'],
        (links: LibraryLinkResource[] | undefined) =>
          links!.filter((link) => link.id !== id),
      );

      alerts.success('Library link was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>
        <Content>
          <Thumbnail src={libraryLink.thumbnail} />
          <ContentText>
            <Title $type="smallBold" as="h4">
              {libraryLink.name}
            </Title>
            <Text
              as="a"
              target="_blank"
              href={libraryLink.link}
              $type="smallBold"
              $color="lightBlue"
            >
              {libraryLink.link}
            </Text>
          </ContentText>
        </Content>
      </Td>
      <Td>
        <Text $type="smallMedium">{libraryLink.notes}</Text>
      </Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              onClick={() => setShowEdit(true)}
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton onClick={onDelete} as="button">
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
          <AnimatePresence>
            {showEdit && (
              <LinkFormModal
                onClose={() => setShowEdit(false)}
                linkType={libraryLink.type}
                libraryLink={libraryLink}
              />
            )}
          </AnimatePresence>
        </ActionsWrap>
      </Td>
    </tr>
  );
};

export default LinksTableRow;
